import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as Flags from 'country-flag-icons/react/3x2';
import { Globe, Save, Languages as LanguagesIcon, Star } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Flag = ({ code, className }) => {
  const C = code ? Flags[code.toUpperCase()] : null;
  return C ? <C className={className} /> : <Globe className={className} />;
};

// Süperadmin: her restorana desteklediği dil setini ve varsayılan dili atar.
const RestaurantLanguagesManagement = () => {
  const { restaurants } = useAuth();

  const [catalog, setCatalog] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [defaultCode, setDefaultCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  });

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/languages`);
        if (res.ok) setCatalog(await res.json());
      } catch (err) {
        console.error('Dil kataloğu yüklenemedi:', err);
      }
    };
    fetchCatalog();
  }, []);

  useEffect(() => {
    if (!selectedRestaurant) {
      setSelectedCodes([]);
      setDefaultCode('');
      return;
    }
    const fetchRestaurantLangs = async () => {
      setLoading(true);
      setMessage(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/restaurants/${selectedRestaurant}/languages`);
        if (res.ok) {
          const data = await res.json();
          setSelectedCodes(data.map((l) => l.code));
          const def = data.find((l) => l.isDefault);
          setDefaultCode(def ? def.code : data[0]?.code || '');
        }
      } catch (err) {
        console.error('Restoran dilleri yüklenemedi:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurantLangs();
  }, [selectedRestaurant]);

  const toggleCode = (code) => {
    setSelectedCodes((prev) => {
      if (prev.includes(code)) {
        const next = prev.filter((c) => c !== code);
        if (defaultCode === code) setDefaultCode(next[0] || '');
        return next;
      }
      const next = [...prev, code];
      if (!defaultCode) setDefaultCode(code);
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedRestaurant) {
      setMessage({ type: 'error', text: 'Önce bir restoran seçin.' });
      return;
    }
    if (selectedCodes.length === 0) {
      setMessage({ type: 'error', text: 'En az bir dil seçin.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/restaurants/${selectedRestaurant}/languages`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ codes: selectedCodes, defaultCode }),
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Diller kaydedildi.' });
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.message || 'Kaydetme sırasında hata oluştu.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'API bağlantı hatası.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200 p-6 flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <LanguagesIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Restoran Dilleri</h1>
            <p className="text-gray-600">Her restoranın desteklediği dilleri ve varsayılan dili belirleyin.</p>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Restoran seçimi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Restoran</label>
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Restoran seçin...</option>
              {(restaurants || []).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          {message && (
            <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
              {message.text}
            </div>
          )}

          {selectedRestaurant && (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    Dilleri seçin. Yıldız ikonuyla varsayılan dili belirleyin (menü içeriğinin temel dili).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {catalog.map((lang) => {
                      const checked = selectedCodes.includes(lang.code);
                      const isDefault = defaultCode === lang.code;
                      return (
                        <div
                          key={lang.code}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${checked ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCode(lang.code)}
                            className="h-4 w-4 text-blue-600"
                          />
                          <Flag code={lang.countryCode} className="h-5 w-7 rounded-sm object-cover flex-shrink-0" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-gray-900 truncate">{lang.nativeName}</span>
                            <span className="block text-xs text-gray-500 truncate">{lang.name} ({lang.code})</span>
                          </span>
                          {checked && (
                            <button
                              type="button"
                              onClick={() => setDefaultCode(lang.code)}
                              title="Varsayılan yap"
                              className={`p-1 rounded-full transition-colors ${isDefault ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                            >
                              <Star className="h-5 w-5" fill={isDefault ? 'currentColor' : 'none'} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                    >
                      <Save className="h-4 w-4" />
                      <span>{saving ? 'Kaydediliyor...' : 'Kaydet'}</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantLanguagesManagement;
